import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import {
  Plus,
  Truck,
  Loader2,
  CalendarIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormDrawer } from "@/components/ui/form-drawer";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";

interface Shipment {
  id: string;
  shipmentNumber: string;
  direction: string;
  mode: string | null;
  status: string;
  purchaseOrderId: string | null;
  orderId: string | null;
  trackingNumber: string | null;
  shipDate: string | null;
  estimatedArrival: string | null;
  carrier: string | null;
  carrierName?: string | null;
  purchaseOrder?: { poNumber: string };
  order?: { orderNumber: string; account?: { name: string } };
  vendor?: { name: string };
}

interface ShipmentsResponse {
  shipments: Shipment[];
  total: number;
}

interface PO {
  id: string;
  poNumber: string;
  status: string;
}
interface Order {
  id: string;
  orderNumber: string;
  status: string;
}
interface Warehouse {
  id: string;
  name: string;
  warehouseCode: string;
  code?: string;
}
interface Carrier {
  id: string;
  name: string;
  code: string | null;
}

const DIRECTION_LABELS: Record<string, string> = {
  INBOUND: "Inbound",
  OUTBOUND: "Outbound",
  DROP_SHIP: "Drop Ship",
  TRANSFER: "Transfer",
};

const STATUS_OPTIONS = [
  { value: "PLANNED", label: "Planned" },
  { value: "BOOKED", label: "Booked" },
  { value: "PICKED_UP", label: "Picked Up" },
  { value: "IN_TRANSIT", label: "In Transit" },
  { value: "DELIVERED", label: "Delivered" },
  { value: "EXCEPTION", label: "Exception" },
  { value: "CANCELLED", label: "Cancelled" },
];

const shipmentFormSchema = z.object({
  direction: z.enum(["INBOUND", "OUTBOUND", "DROP_SHIP", "TRANSFER"]),
  mode: z.string().optional(),
  purchaseOrderId: z.string().optional(),
  orderId: z.string().optional(),
  originWarehouseId: z.string().optional(),
  destinationWarehouseId: z.string().optional(),
  carrier: z.string().optional(),
  carrierId: z.string().optional(),
  trackingNumber: z.string().optional(),
  containerNumber: z.string().optional(),
  proNumber: z.string().optional(),
  billOfLading: z.string().optional(),
  bookingNumber: z.string().optional(),
  scheduledPickup: z.date().optional(),
  scheduledArrival: z.date().optional(),
  notes: z.string().optional(),
});

type ShipmentFormData = z.infer<typeof shipmentFormSchema>;

export function ShipmentStatusBadge({ status }: { status: string }) {
  const config: Record<string, string> = {
    PLANNED: "bg-gray-500 text-white",
    BOOKED: "bg-blue-600 text-white",
    PICKED_UP: "bg-indigo-600 text-white",
    IN_TRANSIT: "bg-purple-600 text-white",
    AT_PORT_OF_ORIGIN: "bg-blue-600 text-white",
    ON_WATER: "bg-blue-600 text-white",
    AT_PORT_OF_DESTINATION: "bg-blue-600 text-white",
    IN_CUSTOMS: "bg-amber-600 text-white",
    OUT_FOR_DELIVERY: "bg-amber-600 text-white",
    DELIVERED: "bg-green-600 text-white",
    PARTIALLY_DELIVERED: "bg-amber-600 text-white",
    EXCEPTION: "bg-red-600 text-white",
    CANCELLED: "bg-gray-600 text-white",
  };
  const label = status
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <Badge className={cn(config[status] || "bg-gray-500 text-white", "hover:opacity-90")} variant="default">
      {label}
    </Badge>
  );
}

export function ShipmentsPage() {
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  const [search, setSearch] = useState("");
  const [directionFilter, setDirectionFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<ShipmentsResponse>({
    queryKey: [
      "/api/shipments",
      { search, direction: directionFilter, status: statusFilter },
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (directionFilter !== "all") params.set("direction", directionFilter);
      if (statusFilter !== "all") params.set("status", statusFilter);
      const qs = params.toString();
      return api.get<ShipmentsResponse>(
        `/shipments${qs ? `?${qs}` : ""}`,
        sessionId,
      );
    },
  });

  const { data: pos = [] } = useQuery<{ purchaseOrders: PO[] }>({
    queryKey: ["/api/purchase-orders", "for-shipments"],
    queryFn: () => api.get("/purchase-orders", sessionId),
    enabled: drawerOpen,
  });

  const { data: orders = [] } = useQuery<{ orders: Order[] }>({
    queryKey: ["/api/orders", "for-shipments"],
    queryFn: () => api.get("/orders", sessionId),
    enabled: drawerOpen,
  });

  const { data: warehouses = [] } = useQuery<Warehouse[]>({
    queryKey: ["/api/warehouses"],
    queryFn: () => api.get<Warehouse[]>("/warehouses", sessionId),
    enabled: drawerOpen,
  });

  const { data: carriers = [] } = useQuery<Carrier[]>({
    queryKey: ["/api/carriers"],
    queryFn: () => api.get<Carrier[]>("/carriers", sessionId),
    enabled: drawerOpen,
  });

  const shipments = data?.shipments || [];

  const form = useForm<ShipmentFormData>({
    resolver: zodResolver(shipmentFormSchema),
    defaultValues: { direction: "INBOUND" },
  });

  const selectedDirection = form.watch("direction");

  const createMutation = useMutation({
    mutationFn: async (data: ShipmentFormData) => {
      return api.post(
        "/shipments",
        {
          ...data,
          scheduledPickup: data.scheduledPickup
            ? data.scheduledPickup.toISOString()
            : null,
          scheduledArrival: data.scheduledArrival
            ? data.scheduledArrival.toISOString()
            : null,
        },
        sessionId,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/shipments"] });
      toast({
        title: "Shipment created",
        description: "The shipment has been created successfully.",
      });
      setDrawerOpen(false);
      form.reset({ direction: "INBOUND" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create shipment",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ShipmentFormData) => {
    createMutation.mutate(data);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Shipments"
        description="Track inbound and outbound logistics"
        actions={
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search by number, tracking, container..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-80"
            />
            <Button onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Shipment
            </Button>
          </div>
        }
      />

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <Tabs value={directionFilter} onValueChange={setDirectionFilter}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="INBOUND">Inbound</TabsTrigger>
            <TabsTrigger value="OUTBOUND">Outbound</TabsTrigger>
            <TabsTrigger value="DROP_SHIP">Drop Ship</TabsTrigger>
            <TabsTrigger value="TRANSFER">Transfer</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs font-medium uppercase tracking-wide">
                Shipment #
              </TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wide">
                Direction
              </TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wide">
                Linked PO / Order
              </TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wide">
                Carrier
              </TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wide">
                Tracking
              </TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wide">
                Status
              </TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wide">
                Ship Date
              </TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wide">
                ETA
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mx-auto" />
                </TableCell>
              </TableRow>
            ) : shipments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <Truck className="h-12 w-12 text-muted-foreground/50 mx-auto mb-2" />
                  <p className="text-muted-foreground">No shipments found</p>
                </TableCell>
              </TableRow>
            ) : (
              shipments.map((s) => (
                <TableRow
                  key={s.id}
                  onClick={() => setLocation(`/shipments/${s.id}`)}
                  className="cursor-pointer hover:bg-muted/30"
                >
                  <TableCell className="font-medium">
                    {s.shipmentNumber}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {DIRECTION_LABELS[s.direction] || s.direction}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {s.purchaseOrder?.poNumber ? (
                      <span className="text-blue-600">
                        PO {s.purchaseOrder.poNumber}
                      </span>
                    ) : s.order?.orderNumber ? (
                      <span className="text-blue-600">
                        SO {s.order.orderNumber}
                        {s.order.account?.name && (
                          <span className="text-muted-foreground ml-2">
                            ({s.order.account.name})
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>{s.carrierName || s.carrier || "—"}</TableCell>
                  <TableCell>{s.trackingNumber || "—"}</TableCell>
                  <TableCell>
                    <ShipmentStatusBadge status={s.status} />
                  </TableCell>
                  <TableCell>
                    {s.shipDate
                      ? format(new Date(s.shipDate), "MMM d, yyyy")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {s.estimatedArrival
                      ? format(new Date(s.estimatedArrival), "MMM d, yyyy")
                      : "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <FormDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title="New Shipment"
        description="Create a new shipment record."
        formId="create-shipment"
        isPending={createMutation.isPending}
        submitLabel="Create Shipment"
        size="lg"
        data-testid="drawer-new-shipment"
      >
        <Form {...form}>
          <form
            id="create-shipment"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="direction"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Direction *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger variant="flat">
                        <SelectValue placeholder="Select direction" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="INBOUND">Inbound</SelectItem>
                      <SelectItem value="OUTBOUND">Outbound</SelectItem>
                      <SelectItem value="DROP_SHIP">Drop Ship</SelectItem>
                      <SelectItem value="TRANSFER">Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="mode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Transport Mode</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger variant="flat">
                        <SelectValue placeholder="Select mode" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="OCEAN">Ocean</SelectItem>
                      <SelectItem value="AIR">Air</SelectItem>
                      <SelectItem value="RAIL">Rail</SelectItem>
                      <SelectItem value="FTL">FTL (Full Truckload)</SelectItem>
                      <SelectItem value="LTL">LTL (Less Than Truckload)</SelectItem>
                      <SelectItem value="PARCEL">Parcel</SelectItem>
                      <SelectItem value="OWN_FLEET">Own Fleet</SelectItem>
                      <SelectItem value="COURIER">Courier</SelectItem>
                      <SelectItem value="CUSTOMER_PICKUP">Customer Pickup</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {(selectedDirection === "INBOUND" || selectedDirection === "DROP_SHIP") && (
              <FormField
                control={form.control}
                name="purchaseOrderId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Purchase Order</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger variant="flat">
                          <SelectValue placeholder="Select a PO (optional)" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(pos as any).purchaseOrders?.map((po: PO) => (
                          <SelectItem key={po.id} value={po.id}>
                            {po.poNumber}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {(selectedDirection === "OUTBOUND" || selectedDirection === "DROP_SHIP") && (
              <FormField
                control={form.control}
                name="orderId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sales Order</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger variant="flat">
                          <SelectValue placeholder="Select an order (optional)" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(orders as any).orders?.map((o: Order) => (
                          <SelectItem key={o.id} value={o.id}>
                            {o.orderNumber}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {(selectedDirection === "OUTBOUND" || selectedDirection === "TRANSFER") && warehouses.length > 0 && (
              <FormField
                control={form.control}
                name="originWarehouseId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Origin Warehouse</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger variant="flat">
                          <SelectValue placeholder="Select origin warehouse" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {warehouses.map((w) => (
                          <SelectItem key={w.id} value={w.id}>
                            {w.name} ({w.warehouseCode})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {(selectedDirection === "INBOUND" || selectedDirection === "TRANSFER") && warehouses.length > 0 && (
              <FormField
                control={form.control}
                name="destinationWarehouseId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Destination Warehouse</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger variant="flat">
                          <SelectValue placeholder="Select destination warehouse" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {warehouses.map((w) => (
                          <SelectItem key={w.id} value={w.id}>
                            {w.name} ({w.warehouseCode})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="carrierId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Carrier</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger variant="flat">
                        <SelectValue placeholder="Select a carrier (optional)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {carriers.map((ca) => (
                        <SelectItem key={ca.id} value={ca.id}>
                          {ca.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="proNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>PRO Number</FormLabel>
                    <FormControl>
                      <Input variant="flat" placeholder="PRO #" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="scheduledPickup"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Scheduled Pickup</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground",
                            )}
                          >
                            {field.value
                              ? format(field.value, "MMM d, yyyy")
                              : "Pick date"}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="scheduledArrival"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Scheduled Arrival</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground",
                            )}
                          >
                            {field.value
                              ? format(field.value, "MMM d, yyyy")
                              : "Pick date"}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea variant="flat" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </FormDrawer>
    </div>
  );
}
